import { IncomingHttpHeaders, IncomingMessage, ServerResponse } from 'http';
import { ParsedUrlQuery } from 'querystring';

import { InitRequest } from '@/hooks/store';
import {
	getDevice,
	getDeviceConsentAndCheckForServerMaintenance,
	getPrices,
	getUser,
} from '@/server/initState';
import { getGuide } from '@/server/initState/getGuide';
import { getProgram } from '@/server/initState/getProgram';
import { getPromoOffer, PromoOffer } from '@/server/initState/getPromoOffer';
import { getCookieValueFromRes } from '@/server/utils/getCookieValueFromRes';
import { Device } from '@/store/device/types';
import { Guide } from '@/store/guide/types';
import { Prices } from '@/store/prices/types';
import { Program } from '@/store/program/types';
import { User } from '@/store/user/types';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { RequestUserData } from '@/utils/apiRequest/types';
import type { PagesRouterSsrDirectOpts } from '@/utils/experiments/getPagesRouterSsrDirectOpts';
import { ACCEPT_GDPR_COOKIES_KEY } from '@/utils/privacyCookies';

import { parseProgram } from './parseProgram';

interface InitStateMiddleware {
	(
		req: IncomingMessage,
		res: ServerResponse,
		cookies: Record<string, string>,
		headers: IncomingHttpHeaders,
		deviceId: string,
		query: ParsedUrlQuery,
		requests: InitRequest[],
		coupon?: string | string[],
		// Surface (pathname + query string) for the promo-offer fetch. Only pages that
		// currently carry the banner supply this; omitted callers skip the fetch entirely.
		promoOfferSurface?: string,
		evaluationDate?: string,
		// Present → route the init fetches below straight to internal app-api instead of the
		// public authproxy edge. `getProgram`/`getGuide` are deliberately excluded: they have
		// no direct branch yet, so they stay on the authproxy regardless.
		ssrDirectOpts?: PagesRouterSsrDirectOpts,
	): Promise<{
		device: Device | null;
		user: User | null;
		prices: Prices | null;
		requiresGdpr: boolean | null;
		program: Program | null;
		guide: Guide | null;
		promoOffer: PromoOffer | null;
	}>;
}

const isAllowedReq = (requests: InitRequest[], route: InitRequest): boolean => requests.includes(route);

export const initStateMiddleware: InitStateMiddleware = async (
	req,
	res,
	cookies,
	headers,
	deviceId,
	query,
	requests,
	coupon,
	promoOfferSurface,
	evaluationDate,
	ssrDirectOpts,
) => {
	const resCookie = res.getHeaders()['set-cookie']
		? getCookieValueFromRes(res.getHeaders()['set-cookie'], 'calm-user-token')
		: undefined;
	const userData: RequestUserData = getUserData({ req, deviceId, calmUserToken: resCookie });

	const [device, user, prices, deviceConsent, program, guide, promoOffer] = await Promise.allSettled([
		isAllowedReq(requests, InitRequest.DEVICE)
			? getDevice(headers?.['user-agent'], userData, ssrDirectOpts)
			: Promise.reject(),
		isAllowedReq(requests, InitRequest.USER) ? getUser(userData, ssrDirectOpts) : Promise.reject(),
		isAllowedReq(requests, InitRequest.PRICES)
			? getPrices(
					{
						...query,
						coupon,
					},
					userData,
					ssrDirectOpts,
				)
			: Promise.reject(),
		getDeviceConsentAndCheckForServerMaintenance(cookies[ACCEPT_GDPR_COOKIES_KEY], userData, ssrDirectOpts),
		getProgram(query.programId, userData),
		getGuide(query.guideId, userData),
		promoOfferSurface
			? getPromoOffer(promoOfferSurface, userData, ssrDirectOpts, evaluationDate)
			: Promise.resolve(null),
	]);

	return {
		device:
			device.status === 'fulfilled'
				? {
						...device.value,
						requires_gdpr_choice:
							deviceConsent.status === 'fulfilled' ? deviceConsent.value.requires_gdpr_choice : null,
						ip_country: deviceConsent.status === 'fulfilled' ? deviceConsent.value.ip_country : null,
					}
				: null,
		user: user.status === 'fulfilled' ? user.value : null,
		prices: prices.status === 'fulfilled' ? prices.value : null,
		requiresGdpr: deviceConsent.status === 'fulfilled' ? deviceConsent.value.requires_gdpr : null,
		program: parseProgram(program, guide),
		guide: parseProgram(program, guide)?.guides?.[0] ?? null,
		promoOffer: promoOffer.status === 'fulfilled' ? promoOffer.value : null,
	};
};
